/**
 * v4.1 Phase 37.6 — Share dialog for a project.
 *
 * Two-tab surface inside the existing Modal shell:
 *   - Active shares: existing tokens with copy + revoke
 *   - New share: expiry slider + create
 *
 * Expiry bounds come from the tenant's settings (loaded on open). If
 * the settings fetch fails we fall back to the server-side hard cap of
 * 90 days so the slider is still usable.
 *
 * Gated by COLLAB_MODE_ENABLED at the caller (ProjectHeader).
 */

import { useEffect, useState } from "react";
import Modal from "../Modal.jsx";
import { useStore } from "../../store/useStore.js";
import {
  createShareToken,
  listShareTokens,
  revokeShareToken,
} from "../../services/projects.js";
import { getTenantSettings } from "../../services/tenantSettings.js";
import "./ManageMembersDialog.css";

function formatExpiry(epochSeconds) {
  if (!epochSeconds) return "—";
  try {
    return new Date(epochSeconds * 1000).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

export default function ShareDialog({ project, open, onClose }) {
  const pushToast = useStore((s) => s.pushToast);

  const [tab, setTab] = useState("active");
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [expiry, setExpiry] = useState(7);
  const [maxExpiry, setMaxExpiry] = useState(30);

  useEffect(() => {
    if (!open || !project?.id) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      listShareTokens(project.id).catch(() => []),
      // Tenant settings lookup may 403 for non-admins; swallow gracefully.
      getTenantSettings().catch(() => null),
    ])
      .then(([tokens, settings]) => {
        if (cancelled) return;
        setShares(tokens || []);
        if (settings?.shareLinkMaxExpiryDays) {
          setMaxExpiry(settings.shareLinkMaxExpiryDays);
          if (settings.shareLinkDefaultExpiryDays) {
            setExpiry(settings.shareLinkDefaultExpiryDays);
          }
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, project?.id]);

  const handleCreate = async () => {
    setBusy(true);
    try {
      const created = await createShareToken(project.id, { expiresInDays: expiry });
      setShares((prev) => [created, ...prev]);
      pushToast("Share link created", "success");
      setTab("active");
      try {
        await navigator.clipboard.writeText(created.url);
        pushToast("Share URL copied to clipboard", "info");
      } catch {
        /* clipboard not available — copy button still works */
      }
    } catch (err) {
      pushToast(err.message || "Failed to create share link", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      pushToast("Copied", "info");
    } catch {
      pushToast("Unable to copy — select and copy manually", "error");
    }
  };

  const handleRevoke = async (token) => {
    setBusy(true);
    try {
      await revokeShareToken(token);
      setShares((prev) => prev.filter((s) => s.token !== token));
      pushToast("Share link revoked", "success");
    } catch (err) {
      pushToast(err.message || "Failed to revoke", "error");
    } finally {
      setBusy(false);
    }
  };

  if (!project) return null;

  return (
    <Modal open={open} onClose={onClose} title="Share project" wide>
      <div className="manage-members">
        <div className="manage-members-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "active"}
            className={tab === "active" ? "tab-active" : ""}
            onClick={() => setTab("active")}
          >
            Active links ({shares.length})
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "new"}
            className={tab === "new" ? "tab-active" : ""}
            onClick={() => setTab("new")}
          >
            New link
          </button>
        </div>

        {tab === "active" && (
          <div className="tab-panel" role="tabpanel">
            {loading ? (
              <p className="empty-hint">Loading…</p>
            ) : shares.length === 0 ? (
              <p className="empty-hint">
                No active share links. Create one from the New link tab.
              </p>
            ) : (
              <ul className="member-list">
                {shares.map((s) => (
                  <li key={s.token} className="invite-row share-row">
                    <div className="member-info">
                      <div className="member-id share-url" title={s.url}>
                        {s.url}
                      </div>
                      <div className="share-meta">
                        Expires {formatExpiry(s.expiresAt)}
                      </div>
                    </div>
                    <div className="member-actions">
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => handleCopy(s.url)}
                      >
                        Copy
                      </button>
                      <button
                        type="button"
                        className="btn-link-danger"
                        onClick={() => handleRevoke(s.token)}
                        disabled={busy}
                      >
                        Revoke
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === "new" && (
          <div className="tab-panel" role="tabpanel">
            <p className="share-hint">
              Anyone with the link and an email on your organisation&apos;s
              registered domain can view this project. Share links do not
              expose the project outside your tenant.
            </p>
            <label className="share-expiry-row">
              <span>Expires in</span>
              <input
                type="range"
                min="1"
                max={maxExpiry}
                step="1"
                value={expiry}
                onChange={(e) => setExpiry(Number(e.target.value))}
              />
              <span className="share-expiry-value">{expiry} days</span>
            </label>
            <div className="transfer-confirm-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleCreate}
                disabled={busy}
              >
                {busy ? "Creating…" : "Create share link"}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
